import { useCallback, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectUserIsAdmin } from 'redux/reducers/authReducer';
import RelatorioAprovacaoConfigContext from 'hooks/Relatorios/Aprovacao/RelatorioAprovacaoConfigContext';
import useAprovacaoParams from 'hooks/Relatorios/Aprovacao/useAprovacaoParams';
import { RelatorioAprovacaoConfig } from 'helper/Relatorios/Aprovacao/config';
import {
  contarAprovadas,
  percentual as calcularPercentual,
  podePublicar as calcularPodePublicar,
} from 'helper/Relatorios/Aprovacao';
import {
  useAprovarRelatorioSecoesMutation,
  useGetRelatorioAprovacaoQuery,
  usePublicarRelatorioMutation,
  useReabrirRelatorioMutation,
  useRevogarRelatorioSecoesMutation,
} from 'services/api/products/relatorios';
import { AprovacaoParams, AprovacaoSecao, AprovacaoStatus } from 'types/Relatorios/Aprovacao';

/**
 * Estado/ações do fluxo de aprovação/publicação de um informe. Com o informe
 * desabilitado (`config.habilitado()`), `habilitado` é `false`, a query fica
 * `skip`ada e o restante do hook devolve valores neutros — o consumidor
 * (RevisaoBar/PainelAprovacao/PublicacaoGate) trata isso como "flag off,
 * comportamento idêntico ao anterior".
 */
export interface UseRelatorioAprovacao {
  config: RelatorioAprovacaoConfig;
  habilitado: boolean;
  isAdmin: boolean;
  modoRevisao: boolean;
  params: AprovacaoParams;
  status: AprovacaoStatus | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  aprovadas: number;
  total: number;
  percentual: number;
  podePublicar: boolean;
  publicado: boolean;
  statusPorSlug: Record<string, AprovacaoSecao | undefined>;
  aprovar: (secoes: string[]) => Promise<AprovacaoStatus>;
  revogar: (secoes: string[]) => Promise<AprovacaoStatus>;
  publicar: () => Promise<AprovacaoStatus>;
  reabrir: () => Promise<AprovacaoStatus>;
  mutando: boolean;
  erroMutacao: unknown;
  irParaData: (dataRodadaIso: string) => void;
}

/**
 * A config vem do argumento ou, na falta dele, de `RelatorioAprovacaoConfigContext`
 * (fornecido por `RelatorioSecoesView`). Sem nenhum dos dois, lança erro claro.
 */
export default function useRelatorioAprovacao(
  configArg?: RelatorioAprovacaoConfig,
): UseRelatorioAprovacao {
  const configContexto = useContext(RelatorioAprovacaoConfigContext);
  const config = configArg ?? configContexto;
  if (!config) {
    throw new Error(
      'useRelatorioAprovacao: informe a config por argumento ou use dentro de RelatorioAprovacaoConfigContext',
    );
  }

  const dispatch = useDispatch();
  const habilitado = config.habilitado();
  const isAdmin = useSelector(selectUserIsAdmin);
  const modoRevisao = habilitado && isAdmin;
  const params = useAprovacaoParams(config);

  const {
    currentData: status, isLoading: primeiraCarga, isFetching, isError, refetch,
  } = useGetRelatorioAprovacaoQuery(params, { skip: !habilitado });
  // RTK só marca `isLoading` na 1ª carga (há `data` do arg anterior). Na troca de
  // data/variante `currentData` fica `undefined` com `isFetching`: tratar como loading,
  // senão o PainelAprovacao redireciona e gate/barra somem sem spinner.
  const isLoading = status === undefined && (primeiraCarga || isFetching);

  const [aprovarTrigger, aprovarResult] = useAprovarRelatorioSecoesMutation();
  const [revogarTrigger, revogarResult] = useRevogarRelatorioSecoesMutation();
  const [publicarTrigger, publicarResult] = usePublicarRelatorioMutation();
  const [reabrirTrigger, reabrirResult] = useReabrirRelatorioMutation();

  const aprovar = useCallback((secoes: string[]) => (
    aprovarTrigger({ ...params, secoes }).unwrap()
  ), [aprovarTrigger, params]);

  const revogar = useCallback((secoes: string[]) => (
    revogarTrigger({ ...params, secoes }).unwrap()
  ), [revogarTrigger, params]);

  const publicar = useCallback(() => (
    publicarTrigger(params).unwrap()
  ), [publicarTrigger, params]);

  const reabrir = useCallback(() => (
    reabrirTrigger(params).unwrap()
  ), [reabrirTrigger, params]);

  const { setDataAnalisada } = config;
  const irParaData = useCallback((dataRodadaIso: string) => {
    const [ano, mes, dia] = dataRodadaIso.split('-').map(Number);
    dispatch(setDataAnalisada({ ano, mes, dia }));
  }, [dispatch, setDataAnalisada]);

  const secoes = useMemo(() => status?.secoes ?? [], [status]);
  const aprovadas = contarAprovadas(secoes);
  const total = secoes.length;

  const statusPorSlug = useMemo(() => (
    secoes.reduce<Record<string, AprovacaoSecao | undefined>>((acc, secao) => {
      acc[secao.slug] = secao;
      return acc;
    }, {})
  ), [secoes]);

  const mutando = aprovarResult.isLoading || revogarResult.isLoading
    || publicarResult.isLoading || reabrirResult.isLoading;
  const erroMutacao = aprovarResult.error ?? revogarResult.error
    ?? publicarResult.error ?? reabrirResult.error;

  return {
    config,
    habilitado,
    isAdmin,
    modoRevisao,
    params,
    status,
    isLoading,
    isError,
    refetch,
    aprovadas,
    total,
    percentual: calcularPercentual(aprovadas, total),
    podePublicar: status !== undefined && calcularPodePublicar(status),
    publicado: status?.status === 'publicado',
    statusPorSlug,
    aprovar,
    revogar,
    publicar,
    reabrir,
    mutando,
    erroMutacao,
    irParaData,
  };
}
